import {
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  lazy,
  Suspense,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Tab from '@mui/material/Tab'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { EVENTS, CATEGORIES } from '@common/constants/tracking'
import { WorkflowTypes, isCrmWorkflow } from '@common/interfaces/notes'
import { ITask, TaskStatus } from '@common/interfaces/task'
import cn from 'classnames'
import queryString from 'query-string'
import genericSs from '../../assets/styles/generic.module.scss'
import { ROUTES } from '../../constants/routes'
import { ArcScreenSection, arcScreenProps } from '../../helpers/arcVoice/screenText'
import {
  DEFAULT_NOTES_PANEL_WIDTH,
  MAX_NOTES_PANEL_WIDTH,
  MIN_NOTES_PANEL_WIDTH,
  NOTES_OVERLAY_MAX_WIDTH,
  NOTES_PANEL_WIDTH_STORAGE_KEY,
  NotesPanelState,
  NotesPanelTab,
  applyNotesLayoutVars,
  clearNotesLayoutVars,
  isListPageWorkflow,
  isNewsCapableWorkflow,
  notesPanelTabFromParam,
  resolveNotesLayout,
  stripCellNoteContext,
} from '../../helpers/notesPanel'
import { useIsMobile } from '../../hooks/useIsMobile'
import useResizablePanelWidth from '../../hooks/useResizablePanelWidth'
import { useUrlOpenParam } from '../../hooks/useUrlOpenParam'
import TaskRowStatusIcon from '../../pages/MyTasksPage/components/TaskRowStatusIcon'
import { TaskSourceIndicator } from '../../pages/MyTasksPage/components/taskCellRenderers'
import { getTaskStatusClass } from '../../pages/MyTasksPage/getTaskStatusClass'
import {
  formatTaskDueDateLabel,
  isDueTodayOrTomorrow,
  isOverdue,
} from '../../pages/MyTasksPage/taskHelpers'
import { useTaskCompleteConfirm } from '../../pages/MyTasksPage/useTaskCompleteConfirm'
import Text from '../Common/Text'
import TasksEmptyState from '../Tasks/TasksEmptyState'
import NotesRail from './NotesRail'
import styles from './RightSidebar.module.scss'
import { PropsFromRedux } from './index'
import { renderTabIcon } from './tabIcons'

// Every page mounts this sidebar, so each tab is its own chunk, fetched when the tab opens:
// loading them up front put the notes editor (draft-js) and the task drawer on every cold start.
const BBCLogsContainer = lazy(() => import('../BBCLogsContainer'))
const ClientTasksContainer = lazy(() => import('../ClientTasksContainer'))
const FlagContainer = lazy(() => import('../Flags'))
const GeneralAnalysisLogsContainer = lazy(() => import('../GeneralAnalysisLogsContainer'))
const MarketIntelligenceContainer = lazy(() => import('../MarketIntelligenceContainer'))
const NewsContainer = lazy(() => import('../NewsContainer'))
const NotesContainer = lazy(() => import('../Notes'))
const TaskDetailDrawer = lazy(() => import('../Tasks/TaskDetailDrawer'))

/** px the panel grows/shrinks per arrow key press on the resize handle. */
const RESIZE_KEYBOARD_STEP = 16

const TASKS_PREVIEW_EMPTY_DESCRIPTIONS = ['New tasks will appear here as activities are created.']
export interface IProps extends PropsFromRedux {
  workflow: WorkflowTypes
}

const RightSidebar = ({
  isNotesShown,
  notesPanelState,
  notesPanelForcedTab,
  notesCount,
  workflow,
  bbc,
  entityInfo,
  reporting,
  clientInfo,
  ongoingReporting,
  getNewFlagsCount,
  newFlagsCount,
  countNotes,
  toggleNotesOff,
  setNotesPanelState,
  clearNotesPanelForce,
  requestNotesComposer,
  trackVisualizations,
  myTasksPreviewData,
  listMyTasksPreview,
  updateTask,
  currentUserId,
}: IProps) => {
  const { id, clientId } = useParams<{
    id: string
    clientId: string
  }>()
  const isMobile = useIsMobile()
  // Below the desktop threshold an expanded panel overlays the content instead of
  // squeezing it, so dense tables keep their full width on a laptop.
  const isOverlayWidth = useMediaQuery(`(max-width:${NOTES_OVERLAY_MAX_WIDTH - 1}px)`)

  // Shared with the task detail panel. Owns the drag, the clamping, localStorage
  // persistence and the re-clamp when the viewport shrinks.
  const {
    width: notesPanelWidth,
    isResizing,
    handleResizeStart,
    adjustWidth,
  } = useResizablePanelWidth({
    storageKey: NOTES_PANEL_WIDTH_STORAGE_KEY,
    defaultWidth: DEFAULT_NOTES_PANEL_WIDTH,
    minWidth: MIN_NOTES_PANEL_WIDTH,
    maxWidth: MAX_NOTES_PANEL_WIDTH,
  })
  const [tabValue, setTabValue] = useState<NotesPanelTab | null>(null)
  const { search, pathname, hash } = useLocation()
  const { noteId, cellNoteContext } = useMemo(
    () =>
      queryString.parse(search, {
        parseBooleans: true,
      }) as {
        noteId: string
        cellNoteContext: string
      },
    [search],
  )

  const navigate = useNavigate()
  const noteIdRef = useRef(noteId)

  useEffect(() => {
    noteIdRef.current = noteId
  }, [noteId])

  const workflowConditions = useMemo(
    () => ({
      isBBC: workflow === WorkflowTypes.bbc && bbc?.id && bbc.id === id,
      isEntity: workflow === WorkflowTypes.entityPage && entityInfo?.id,
      isOps: workflow === WorkflowTypes.opsFlow && reporting?.id,
      isClientPage: workflow === WorkflowTypes.clientPage && clientInfo?.id,
      isOngoingReporting: workflow === WorkflowTypes.ongoingReporting && ongoingReporting?.id,
      isDueDiligence: workflow === WorkflowTypes.dueDiligencePage && clientInfo?.id,
      isClientManagement: workflow === WorkflowTypes.clientManagement,
      // Home, Clients and the BD prospects queue: list pages with no record
      // behind them, so no notes and no flags.
      isListPage: isListPageWorkflow(workflow),
      isCrmPage: isCrmWorkflow(workflow),
    }),
    [workflow, bbc?.id, id, entityInfo?.id, reporting?.id, clientInfo?.id, ongoingReporting?.id],
  )

  // The general-analysis Logs tab shows on every surface backed by a
  // general_analyses record with an event log (GeneralAnalysisLogsContainer):
  // the Amendment Analysis page, the prospect Deal Summary
  // (`/prospects/:id/summary`), and the Loan Committee Report.
  const showGeneralAnalysisLogs =
    pathname.includes('/amendment-analysis/') ||
    pathname.includes('/loan-committee-report') ||
    (pathname.includes('/prospects/') && pathname.endsWith('/summary'))

  // The single source of truth for which tabs exist, in render order. Both the
  // docked TabList and the collapsed rail read this, so the two can't drift —
  // they previously duplicated these conditions and had already diverged on
  // Logs (the rail checked only the amendment-analysis path, missing the
  // prospect Deal Summary that showGeneralAnalysisLogs also covers).
  const availableTabs = useMemo<NotesPanelTab[]>(() => {
    const list: NotesPanelTab[] = []
    const { isListPage, isCrmPage } = workflowConditions
    if (!isListPage) {
      list.push(NotesPanelTab.notes)
    }
    if (!isListPage && !isCrmPage) {
      list.push(NotesPanelTab.flags)
    }
    if (workflow === WorkflowTypes.clientPage) {
      list.push(NotesPanelTab.tasks)
    }
    if (isNewsCapableWorkflow(workflow)) {
      list.push(NotesPanelTab.marketIntel)
      list.push(NotesPanelTab.news)
    }
    if (
      [WorkflowTypes.clientPage, WorkflowTypes.bbc].includes(workflow) ||
      showGeneralAnalysisLogs
    ) {
      list.push(NotesPanelTab.logs)
    }
    return list
  }, [workflowConditions, workflow, showGeneralAnalysisLogs])

  // Whatever is selected has to be on offer, so navigating away from the surface
  // a tab belongs to lands on a real one instead of leaving the strip with no
  // selection. Reads `availableTabs` rather than restating its conditions — the
  // hand-listed version only covered News, Market Intel and Tasks, and said
  // nothing about Flags or Logs.
  useEffect(() => {
    if (!workflow || !availableTabs.length) return
    if (!availableTabs.includes(tabValue)) setTabValue(availableTabs[0])
  }, [workflow, tabValue, availableTabs])

  const handleOpenFromRail = useCallback(
    (tab: NotesPanelTab, isQuickAdd?: boolean) => {
      setTabValue(tab)
      setNotesPanelState(NotesPanelState.docked)
      if (isQuickAdd) {
        requestNotesComposer()
      }
      void trackVisualizations(
        isQuickAdd ? EVENTS.notesQuickAdd : EVENTS.notesOpen,
        CATEGORIES.notesSidebar,
        { data: { tab, workflow, source: 'rail' } },
      )
    },
    [setNotesPanelState, requestNotesComposer, trackVisualizations, workflow],
  )

  // A rail with no tabs has nothing to show, so it must not render — and must not
  // reserve its 56px either, or it silently narrows the page for an empty strip.
  // Single source of truth for both, so the two can't disagree.
  const isRailVisible = notesPanelState === NotesPanelState.rail && availableTabs.length > 0

  // Laptop widths overlay instead of squeezing — except for a cell-note deep link,
  // which must keep the annotated cell visible, so it accepts the squeeze.
  const isPanelOverlaying = isNotesShown && isOverlayWidth && !isMobile && !cellNoteContext

  // Publish the column's footprint to CSS. Every `100vw`-based width calc in the
  // app reads these, so they must reflect rail vs docked vs overlay — not just
  // "open or closed" — and must reset when the sidebar unmounts.
  useEffect(() => {
    applyNotesLayoutVars(
      resolveNotesLayout({
        state: notesPanelState,
        width: notesPanelWidth,
        isOverlay: isPanelOverlaying,
        isMobile,
        isRailVisible,
      }),
    )
  }, [notesPanelState, notesPanelWidth, isPanelOverlaying, isMobile, isRailVisible])

  useEffect(() => clearNotesLayoutVars, [])

  // Counts exist for the rail, which is the only notes affordance on a collapsed
  // page — so they are fetched whenever the column is collapsed, including on
  // mount, and refreshed on collapsing again so an added or resolved note shows.
  // Gating on the rail state also keeps this off client-portal pages, which always
  // resolve to docked and whose users lack permission for the count service.
  // Diligence document routes nest the record under `:clientId`; `:id` there is the
  // reporting flow, which the notes scope can't resolve — same lookup NotesContainer uses.
  const notesScopeId = clientId || id
  useEffect(() => {
    if (notesScopeId && workflow && notesPanelState === NotesPanelState.rail) {
      void countNotes(notesScopeId, { workflow })
    }
  }, [notesScopeId, workflow, notesPanelState, countNotes])

  const removeCellNoteContext = useCallback(() => {
    if (cellNoteContext) {
      void navigate(
        {
          pathname,
          search: stripCellNoteContext(search),
          hash,
        },
        {
          replace: true,
        },
      )
    }
    // Dismissing the deep-link context releases the latch, so the page's own
    // default (often the rail) takes over again — a forced open is temporary.
    clearNotesPanelForce()
  }, [search, hash, navigate, pathname, cellNoteContext, clearNotesPanelForce])

  // Collapsing the panel ends the cell-note visit, so the scope must go with it.
  // That scope lives ONLY in the URL, and nothing on the close path cleared it —
  // so reopening from the rail (quick-add or the Notes icon) came back still
  // filtered to the old cell. A route change was the only thing dropping the
  // param, which is exactly why the bug never reproduced across pages.
  //
  // Keyed on the docked -> collapsed TRANSITION rather than "not docked": a plain
  // state check would strip the param on mount, before the deep-link latch has
  // docked the panel, killing the very scope the link asked for.
  const wasDockedRef = useRef(isNotesShown)
  useEffect(() => {
    const wasDocked = wasDockedRef.current
    wasDockedRef.current = isNotesShown
    if (wasDocked && !isNotesShown && cellNoteContext) {
      removeCellNoteContext()
    }
  }, [isNotesShown, cellNoteContext, removeCellNoteContext])

  const handleChangeTab = useCallback(
    (event: ChangeEvent<object>, newValue: NotesPanelTab) => {
      if (newValue !== NotesPanelTab.notes && cellNoteContext) {
        removeCellNoteContext()
      }
      setTabValue(newValue)
      void trackVisualizations(EVENTS.notesTab, CATEGORIES.notesSidebar, {
        data: { tab: newValue, workflow, source: 'panel' },
      })
    },
    [cellNoteContext, removeCellNoteContext, trackVisualizations, workflow],
  )

  useEffect(() => {
    if (id && workflowConditions?.isBBC && !noteIdRef.current && !cellNoteContext) {
      setTabValue(NotesPanelTab.flags)
    } else if (workflowConditions?.isListPage) {
      // News is the only tab a list page offers, so it is also its landing tab.
      setTabValue(NotesPanelTab.news)
    } else {
      setTabValue(NotesPanelTab.notes)
    }
  }, [
    id,
    workflowConditions?.isBBC,
    workflowConditions?.isListPage,
    workflowConditions?.isClientPage,
    workflowConditions?.isEntity,
    cellNoteContext,
  ])

  useEffect(() => {
    if (cellNoteContext) {
      setTabValue(NotesPanelTab.notes)
    }
  }, [cellNoteContext])

  // A force-open can name the tab it wants (Flags for a flag jump). Declared after
  // the workflow-default effects above so it wins over them. Ignored when this
  // page doesn't offer that tab — selecting an absent tab renders an empty panel.
  useEffect(() => {
    if (notesPanelForcedTab && availableTabs.includes(notesPanelForcedTab as NotesPanelTab)) {
      setTabValue(notesPanelForcedTab as NotesPanelTab)
    }
  }, [notesPanelForcedTab, availableTabs])

  // `?panel=` is one-shot like `?noteId=`: MainLayout latches the open, this picks the tab and
  // strips the param, so the same link followed again still lands even if the latched tab is
  // unchanged.
  const [panelParam, setPanelParam] = useUrlOpenParam('panel')
  const requestedTab = workflow ? notesPanelTabFromParam(panelParam) : null
  useEffect(() => {
    if (!requestedTab) return
    if (availableTabs.includes(requestedTab)) setTabValue(requestedTab)
    setPanelParam(null)
  }, [requestedTab, availableTabs, setPanelParam])

  const handleCloseSidebar = useCallback(() => {
    if (toggleNotesOff) {
      toggleNotesOff()
    }
  }, [toggleNotesOff])

  // Keyboard equivalent to the drag handle — a resize affordance that only answers
  // to a mouse isn't one for everyone. The drag itself, clamping, persistence and
  // the re-clamp on viewport resize all live in useResizablePanelWidth.
  const handleResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return
      }
      event.preventDefault()
      // Anchored right, so widening means moving the edge left.
      const step = event.key === 'ArrowLeft' ? RESIZE_KEYBOARD_STEP : -RESIZE_KEYBOARD_STEP
      adjustWidth(notesPanelWidth + step)
    },
    [notesPanelWidth, adjustWidth],
  )

  // Report the settled width once the drag ends rather than on every mouse move,
  // so tracking gets one row per resize instead of hundreds.
  const wasResizingRef = useRef(isResizing)
  useEffect(() => {
    if (wasResizingRef.current && !isResizing) {
      void trackVisualizations(EVENTS.notesResize, CATEGORIES.notesSidebar, {
        data: { workflow, width: notesPanelWidth },
      })
    }
    wasResizingRef.current = isResizing
  }, [isResizing, notesPanelWidth, trackVisualizations, workflow])

  useEffect(() => {
    // CRM pages have no flags — the Flags tab is hidden for them
    if (id && workflow && !workflowConditions.isCrmPage) {
      void getNewFlagsCount({
        id,
        workflow,
      })
    }
  }, [id, workflow, workflowConditions.isCrmPage, getNewFlagsCount])

  // The Tasks tab's detail panel is hosted here rather than inside the tab: MUI's
  // TabPanel unmounts its children when another tab is selected, which would make
  // an open task vanish the moment the user glanced at Notes.
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  // Mounted on first use and kept: the panel loads its own option lists (companies,
  // categories, assignees) on mount, so rendering it on every page with a sidebar
  // would spend those requests on users who never open a task. Keeping it after
  // that is what lets the close animation play.
  const [isTaskPanelMounted, setIsTaskPanelMounted] = useState(false)
  // Bumped on every mutation the panel makes; ClientTasksContainer refetches on the
  // change, since an edit there can move a task out of this client's open backlog.
  const [tasksReloadToken, setTasksReloadToken] = useState(0)

  // Staged in two renders rather than opened outright. MUI's Drawer passes
  // `appear: mounted.current` to its Slide, which is false on the drawer's first
  // render — so a panel whose first render already has `open` true skips the
  // transition and simply appears. Mount it closed, then open it once it is on
  // the page, which is what the pre-hoist version got for free by always being
  // mounted.
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null)

  // `?taskId=` on the client page opens that task here, the same as clicking it in the Tasks tab.
  const [taskIdParam, setTaskIdParam] = useUrlOpenParam('taskId')
  const urlTaskId = workflow === WorkflowTypes.clientPage ? taskIdParam : null

  const stageTask = useCallback((taskId: string) => {
    setIsTaskPanelMounted(true)
    setPendingTaskId(taskId)
  }, [])

  const handleOpenTask = useCallback(
    (task: ITask) => {
      stageTask(task.id)
      setTaskIdParam(task.id)
    },
    [stageTask, setTaskIdParam],
  )

  useEffect(() => {
    if (!pendingTaskId || !isTaskPanelMounted) return
    setOpenTaskId(pendingTaskId)
    setPendingTaskId(null)
  }, [pendingTaskId, isTaskPanelMounted])

  const handleCloseTask = useCallback(() => {
    setOpenTaskId(null)
    setTaskIdParam(null)
  }, [setTaskIdParam])

  const handleTaskChanged = useCallback(() => setTasksReloadToken((token) => token + 1), [])

  // The sidebar outlives both client and page navigation, so an open panel would
  // otherwise survive the switch — showing the previous client's task, or staying
  // pinned over an unrelated page for the same client, since the clientInfo slice
  // is not reset on navigation. Only the client page can open one, so any workflow
  // change is a safe clear (the tab strip does the same at the effect above). A task the URL
  // names survives it, since the client record lands after the param on a fresh load.
  const sidebarClientName = clientInfo?.clientName ?? null
  useEffect(() => {
    if (urlTaskId) {
      stageTask(urlTaskId)
      return
    }
    setOpenTaskId(null)
    setPendingTaskId(null)
  }, [sidebarClientName, workflow, urlTaskId, stageTask])

  // Deliberately excludes the homepage: internal roles now land on the home
  // dashboard, whose My Work widget covers this ground in more detail. Showing
  // both would put two task lists on one screen.
  const showTasksPreview = workflowConditions.isClientManagement

  useEffect(() => {
    if (showTasksPreview && isNotesShown && currentUserId) {
      void listMyTasksPreview({
        orderBy: 'due_date',
        orderDirection: 'ASC',
        page: 0,
        perPage: 4,
        filters: { assignedToUserId: [currentUserId] },
      })
    }
  }, [showTasksPreview, isNotesShown, listMyTasksPreview, currentUserId])

  const previewTasks = myTasksPreviewData?.data?.tasks || []
  const completingIdsRef = useRef(new Set<string>())

  const refetchPreview = useCallback(() => {
    if (currentUserId) {
      void listMyTasksPreview({
        orderBy: 'due_date',
        orderDirection: 'ASC',
        page: 0,
        perPage: 4,
        filters: { assignedToUserId: [currentUserId] },
      })
    }
  }, [listMyTasksPreview, currentUserId])

  const { requestTaskComplete, completeConfirmModal } = useTaskCompleteConfirm()

  const handleTaskMarkComplete = useCallback(
    async (task: ITask) => {
      if (completingIdsRef.current.has(task.id)) {
        return
      }
      await requestTaskComplete(task, async (options) => {
        completingIdsRef.current.add(task.id)
        try {
          await updateTask(task.id, { status: TaskStatus.Complete, ...options })
          refetchPreview()
        } finally {
          completingIdsRef.current.delete(task.id)
        }
      })
    },
    [updateTask, refetchPreview, requestTaskComplete],
  )

  const handleTaskClick = useCallback(() => {
    void navigate(ROUTES.MY_TASKS)
  }, [navigate])

  if (!tabValue) {
    return null
  }

  return (
    <>
      <div
        className={cn(styles.mobileOverlay, {
          [styles.overlayVisible]: isNotesShown,
          // On laptop widths the panel floats over the content, so it needs the
          // same click-away scrim mobile already had.
          [styles.laptopOverlay]: isPanelOverlaying,
        })}
        onClick={handleCloseSidebar}
      />
      {isRailVisible && (
        <NotesRail
          tabs={availableTabs}
          newFlagsCount={newFlagsCount}
          unreadCount={notesCount?.unread ?? null}
          needsActionCount={notesCount?.needsAction ?? 0}
          onOpen={handleOpenFromRail}
        />
      )}
      <Drawer
        className={cn(styles.drawer, {
          [styles.expanded]: isNotesShown,
          [styles.overlaying]: isPanelOverlaying,
          [styles.behindTaskPanel]: !!openTaskId,
        })}
        classes={{
          paper: styles.drawerPaper,
        }}
        variant="persistent"
        anchor="right"
        open={isNotesShown}
        {...arcScreenProps(isNotesShown ? ArcScreenSection.SidePanel : null)}
      >
        {isNotesShown && !isMobile && (
          <div
            className={styles.resizeHandle}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize notes panel"
            aria-valuenow={notesPanelWidth}
            tabIndex={0}
            data-cy="notes-resize-handle"
            onMouseDown={handleResizeStart}
            onKeyDown={handleResizeKeyDown}
          />
        )}
        {showTasksPreview && (
          <Box
            data-cy="my-tasks-preview"
            className={cn(styles.tasksPreviewSection, {
              [styles.tasksPreviewSectionEmpty]: previewTasks.length === 0,
            })}
          >
            <TabContext value="tasks">
              <Box>
                <TabList classes={{ indicator: styles.tabIndicator }}>
                  <Tab
                    label={
                      <Text variant="tab" component="div" className={styles.tabName}>
                        My tasks
                      </Text>
                    }
                    value="tasks"
                    className={styles.tabNameContainer}
                  />
                </TabList>
              </Box>
              <TabPanel value="tasks" className={cn(styles.tabPanel, styles.tasksPreviewPanel)}>
                {previewTasks.length === 0 ? (
                  <TasksEmptyState
                    data-cy="my-tasks-preview-empty"
                    descriptions={TASKS_PREVIEW_EMPTY_DESCRIPTIONS}
                    actionLabel="+ Add Task"
                    onAction={handleTaskClick}
                  />
                ) : (
                  <Box className={styles.tasksPreviewList}>
                    {previewTasks.map((task, index) => (
                      <Box key={task.id}>
                        {index > 0 && <Divider className={styles.taskPreviewDivider} />}
                        <Box
                          className={styles.taskPreviewRow}
                          data-cy="my-tasks-preview-row"
                          onClick={handleTaskClick}
                          role="button"
                          tabIndex={0}
                        >
                          <Box className={styles.taskPreviewIconAndTitle}>
                            <Box
                              className={styles.taskPreviewIcon}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TaskRowStatusIcon
                                task={task}
                                isComplete={task.status === TaskStatus.Complete}
                                isRowSelected={false}
                                onMarkComplete={handleTaskMarkComplete}
                              />
                            </Box>
                            <Text
                              variant="title"
                              component="p"
                              casing="none"
                              className={styles.taskPreviewTitle}
                            >
                              {task.title}
                            </Text>
                          </Box>
                          <Box className={styles.taskPreviewMeta}>
                            <span
                              className={cn(
                                genericSs.reportingStatus,
                                getTaskStatusClass(task.status),
                                styles.taskPreviewStatus,
                              )}
                            >
                              {task.status || TaskStatus.NotStarted}
                            </span>
                            <TaskSourceIndicator task={task} />
                            <Text
                              variant="bodySm"
                              component="span"
                              casing="none"
                              tone={
                                isDueTodayOrTomorrow(task)
                                  ? 'success'
                                  : isOverdue(task)
                                    ? 'error'
                                    : 'secondary'
                              }
                            >
                              {task.dueDate ? formatTaskDueDateLabel(task) : '—'}
                            </Text>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </TabPanel>
            </TabContext>
          </Box>
        )}
        <TabContext value={tabValue}>
          <Box>
            <TabList onChange={handleChangeTab} classes={{ indicator: styles.tabIndicator }}>
              {availableTabs.map((tab) => (
                <Tab
                  key={tab}
                  value={tab}
                  aria-label={tab}
                  className={styles.tabIcon}
                  icon={
                    <Tooltip title={tab} placement="bottom">
                      <Badge
                        classes={{ badge: styles.tabBadge }}
                        badgeContent={newFlagsCount}
                        invisible={tab !== NotesPanelTab.flags || newFlagsCount === 0}
                      >
                        {renderTabIcon(tab)}
                      </Badge>
                    </Tooltip>
                  }
                />
              ))}
            </TabList>
          </Box>
          <Suspense fallback={null}>
            <TabPanel
              value={NotesPanelTab.notes}
              className={cn(styles.tabPanel, styles.notesTabPanel)}
            >
              <NotesContainer />
            </TabPanel>
            <TabPanel value={NotesPanelTab.flags} className={styles.tabPanel}>
              <FlagContainer workflow={workflow} />
            </TabPanel>
            <TabPanel value={NotesPanelTab.news} className={styles.tabPanel}>
              <NewsContainer workflow={workflow} />
            </TabPanel>
            <TabPanel value={NotesPanelTab.marketIntel} className={styles.tabPanel}>
              <MarketIntelligenceContainer workflow={workflow} />
            </TabPanel>
            <TabPanel value={NotesPanelTab.logs} className={styles.tabPanel}>
              {showGeneralAnalysisLogs ? (
                <GeneralAnalysisLogsContainer />
              ) : (
                <BBCLogsContainer workflow={workflow} />
              )}
            </TabPanel>
            <TabPanel value={NotesPanelTab.tasks} className={styles.tabPanel}>
              <ClientTasksContainer
                clientName={sidebarClientName}
                reloadToken={tasksReloadToken}
                onOpenTask={handleOpenTask}
              />
            </TabPanel>
          </Suspense>
        </TabContext>
      </Drawer>
      {isTaskPanelMounted && (
        <Suspense fallback={null}>
          <TaskDetailDrawer
            taskId={openTaskId}
            onClose={handleCloseTask}
            onOpenTask={handleOpenTask}
            onTaskChanged={handleTaskChanged}
          />
        </Suspense>
      )}
      {completeConfirmModal}
    </>
  )
}

export default RightSidebar
